/* Companion parts: the filter picker, the diagram, the bundle and the quick view.
 *
 * Ported from docs/mockups/pdp-companion-parts.html. The section resolves every part
 * in Liquid and hands this one JSON payload, so picking a filter is instant: the
 * customer has just told us what they own and should not then wait for a fetch.
 *
 * Self contained rather than folded into aps-pdp.js, because a product without the
 * de_filters metafield renders no section and therefore loads neither file.
 */
(function () {
  'use strict';

  /* The nudge in the buy column points at the finder. Smooth scrolling runs off the
     same frame clock as the drawer's slide and a hidden tab freezes it, so ask for
     smooth only where there is a clock to run it. Focus follows the scroll, so a
     keyboard lands where the eye does. */
  function money(c) {
    var s = (c / 100).toFixed(2).split('.');
    return '<span class="aps-pn">$' + Number(s[0]).toLocaleString() + '<sup>' + s[1] + '</sup></span>';
  }
  function flat(c) {
    return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  // Part titles carry inch marks, so anything going into an attribute is escaped.
  function att(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  }
  /* The drawer wants the part at about 300 across; the payload carries it at 120,
     because that is the size the rows and cards on the page actually draw. Rather than
     ship a second URL for every part, ask the CDN for the bigger one here: a Shopify
     image URL states its width in the query and will serve any other. Only one part is
     open in the drawer at a time, so this runs once per opening.

     If the URL has no width to swap, it comes back untouched, which is the right answer
     for a source that is not a Shopify CDN URL. */
  function bigOf(u) {
    return u && /([?&]width=)\d+/.test(u) ? u.replace(/([?&]width=)\d+/, '$1720') : (u || '');
  }
  function stepper(q) {
    var n = q.querySelector('b'), bs = q.querySelectorAll('button');
    bs[0].addEventListener('click', function () { n.textContent = Math.max(1, +n.textContent - 1); });
    bs[1].addEventListener('click', function () { n.textContent = Math.min(99, +n.textContent + 1); });
  }

  /* ---- the add to cart modal ------------------------------------------------
   *
   * This template does not open the cart drawer. Somebody who has just added a grid
   * set is mid job, and a list of what they already chose is the least useful thing
   * to show them. The modal says what happened in one line and spends the rest of
   * its space on what is missing.
   *
   * It does not reimplement adding. Horizon's own product form does the add and
   * announces it; all this does is stop the drawer opening on that announcement and
   * put itself there instead. The cart is then read back from /cart.js, so the
   * subtotal, the free shipping bar and the deduplication are the real cart rather
   * than a running total kept here.
   */
  function makeCartModal(sec, D) {
    var F = D.filters || {}, L = D.labels || {}, host = sec.closest('.aps-frame') || document.body;
    var wrap = document.createElement('div');
    wrap.innerHTML = '<div class="acs" data-ac-scrim hidden></div>'
      + '<aside class="ac" data-ac hidden role="dialog" aria-modal="true">'
      + '<div class="ach"><span class="actick"><svg viewBox="0 0 24 24" aria-hidden="true">'
      + '<path d="M20 6 9 17l-5-5"/></svg></span>'
      + '<span class="achx"><b data-ac-title></b><span data-ac-sum></span></span>'
      + '<span class="acthumbs" data-ac-thumbs></span>'
      + '<button type="button" class="acx" data-ac-close aria-label="Close">'
      + '<svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg></button></div>'
      + '<div class="acship" data-ac-ship>'
      + '<svg class="tk" viewBox="0 0 24 24" aria-hidden="true">'
      + '<path d="M2.6 6.4h10.9v9.4H2.6z"/><path d="M13.5 9.4h3.7l3.3 3.4v3h-7z"/>'
      + '<circle cx="6.9" cy="18.1" r="1.9"/><circle cx="17.2" cy="18.1" r="1.9"/></svg>'
      + '<span class="lbl" data-ac-ship-t></span>'
      + '<span class="bar"><i data-ac-ship-bar></i></span></div>'
      + '<div class="acbody"><div class="acsec" data-ac-fit hidden>'
      + '<div class="achd"><b>Finish the job</b>'
      + '<span class="acbl" data-ac-fit-logo></span><span data-ac-fit-s></span></div>'
      + '<div class="acrail" data-ac-fit-rail></div></div>'
      + '<div class="acsec" data-ac-univ hidden><div class="achd"><b>Every DE filter needs these</b>'
      + '<span>Whatever tank you have.</span></div>'
      + '<div class="acrail" data-ac-univ-rail></div></div></div>'
      + '<div class="acfoot"><div class="acsub"><span>Cart subtotal</span><b data-ac-sub></b></div>'
      + '<div class="acacts"><button type="button" class="obtn" data-ac-close>Keep shopping</button>'
      + '<a class="obtn big" data-ac-cart>View cart</a></div></div></aside>';
    var scrim = wrap.firstChild, box = wrap.lastChild;
    host.appendChild(scrim); host.appendChild(box);
    var q = function (sel) { return box.querySelector(sel); };
    q('[data-ac-cart]').href = D.cartUrl || '/cart';

    var cart = { items: [], total_price: 0, item_count: 0 }, lastKey = null;

    /* Adding goes through the same endpoint Horizon uses, and then announces itself
       with Horizon's own event so the cart icon and the drawer's contents keep up.
       The drawer will not open on it, because auto-open is taken off below. */
    function post(items) {
      return fetch(D.cart, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      }).then(function (res) {
        document.dispatchEvent(new CustomEvent('shopify:cart:lines-update',
          { bubbles: true, detail: { source: 'aps-companion', action: 'add' } }));
        return res;
      });
    }
    /* The cart is read back rather than tallied here, so the subtotal, the free
       shipping bar and the deduplication are what the cart actually holds. */
    function refresh() {
      return fetch((D.cartUrl || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { cart = c; return c; })
        .catch(function () { return cart; });
    }

    function close() {
      box.hidden = true; scrim.hidden = true;
      document.documentElement.style.overflow = '';
    }
    box.querySelectorAll('[data-ac-close]').forEach(function (b) { b.addEventListener('click', close); });
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) close();
    });

    /* The same product arrives from two directions under different variant ids, so
       the handle is what identifies it. */
    function inCart(o) {
      return cart.items.some(function (c) {
        return String(c.variant_id) === String(o.vid) || (o.u && c.url && c.url.split('?')[0] === o.u);
      });
    }
    function card(o) {
      var got = inCart(o);
      return '<div class="accard' + (got ? ' in' : '') + '" data-vid="' + o.vid + '">'
        + '<span class="im">' + (o.img ? '<img alt="" loading="lazy" src="' + att(o.img) + '">' : '') + '</span>'
        + '<span class="role">' + esc(o.role || 'Part') + '</span>'
        + '<h4>' + esc(o.t) + '</h4>'
        + '<p class="why">' + esc(o.note || '') + '</p>'
        + '<span class="foot"><span class="pp">' + money(o.p) + '</span>'
        + '<button type="button" class="obtn sm" data-ac-add' + (got ? ' disabled' : '') + '>'
        + (got ? '&check; Added' : 'Add') + '</button></span></div>';
    }
    function paintRail(el, items) {
      el.innerHTML = items.map(card).join('');
      el.querySelectorAll('[data-ac-add]').forEach(function (b, i) {
        b.addEventListener('click', function () { addOne(items[i], b); });
      });
    }
    function addOne(o, btn) {
      var label = btn.innerHTML;
      btn.disabled = true; btn.textContent = L.adding || 'Adding';
      post([{ id: o.vid, quantity: 1 }])
        .then(function () { return refresh(); })
        .then(function () { render(lastKey); })
        .catch(function () { btn.disabled = false; btn.innerHTML = label; });
    }

    function render(k) {
      var n = cart.item_count || 0;
      q('[data-ac-title]').textContent = 'Added to your cart';
      q('[data-ac-sum]').innerHTML = n + (n === 1 ? ' item' : ' items') + ' &middot; ' + flat(cart.total_price);
      q('[data-ac-sub]').innerHTML = money(cart.total_price);
      /* A stack of what is in the cart, not a list of it: six rows of confirmation
         would push the thing we want them to look at below the fold. */
      q('[data-ac-thumbs]').innerHTML = cart.items.slice(0, 4).map(function (c) {
        return '<span>' + (c.image ? '<img alt="" src="' + att(c.image) + '">' : '') + '</span>';
      }).join('') + (cart.items.length > 4 ? '<span class="more">+' + (cart.items.length - 4) + '</span>' : '');

      var ship = q('[data-ac-ship]');
      if (D.freeShip > 0) {
        ship.hidden = false;
        var need = D.freeShip - cart.total_price;
        q('[data-ac-ship-bar]').style.width =
          Math.max(0, Math.min(100, cart.total_price / D.freeShip * 100)).toFixed(1) + '%';
        ship.classList.toggle('done', need <= 0);
        q('[data-ac-ship-t]').innerHTML = need > 0
          ? 'You are <b>' + flat(need) + '</b> away from <b>free shipping</b>'
          : '<b>Your order ships free</b>';
      } else ship.hidden = true;

      /* The three every filter needs stay on the list whatever the cart holds, and the
         ones already in it come up ticked rather than being dropped. Dropped, a cart
         that had collected all three, which is what a bundle add leaves behind, emptied
         this rail and hid the heading with it: the panel then had nothing under the
         confirmation and read as broken. A fixed set of three is a checklist, and a
         checklist with the done items deleted is not one.

         The rail above is filtered, and should be: it ranks four out of a whole diagram,
         so there is always something else to put there. */
      var univ = (D.supplies || []).filter(function (u) { return u.vid; });
      q('[data-ac-univ]').hidden = !univ.length;
      paintRail(q('[data-ac-univ-rail]'), univ);

      /* What else off this diagram, given what is already in the cart. Adding one
         part and adding the whole bundle should not offer the same four things, and
         they do not: the bundle's own callouts come first, then everything else
         cheapest first, and anything already bought drops out. */
      var f = k && F[k], picks = [];
      if (f) {
        var rank = {};
        (f.bundle || []).forEach(function (h, i) { rank[h] = i; });
        var seen = {};
        picks = f.parts.filter(function (p) {
          if (p.p == null || p.v >= 2 || !p.vid || inCart(p)) return false;
          if (seen[p.vid]) return false;
          seen[p.vid] = 1; return true;
        }).sort(function (x, y) {
          var rx = rank[x.h], ry = rank[y.h];
          if (rx != null && ry != null) return rx - ry;
          if (rx != null) return -1;
          if (ry != null) return 1;
          return x.p - y.p;
        }).slice(0, 4);
      }
      var fit = q('[data-ac-fit]');
      if (picks.length) {
        fit.hidden = false;
        q('[data-ac-fit-s]').textContent = 'For your ' + f.brand + ' ' + f.name;
        var lg = q('[data-ac-fit-logo]');
        lg.innerHTML = f.logo ? '<img alt="' + att(f.brand) + '" src="' + att(f.logo) + '">' : '';
        lg.hidden = !f.logo;
        paintRail(q('[data-ac-fit-rail]'), picks.map(function (p) {
          return { vid: p.vid, u: p.u, t: p.t, p: p.p, img: p.img,
                   role: p.role || ('Diagram key ' + p.cal),
                   note: p.note || ('Numbered ' + p.cal + ' on your diagram.') };
        }));
      } else fit.hidden = true;
    }

    function open(k) {
      if (k) lastKey = k;
      render(lastKey);
      /* Already showing: the render above is the whole update, and that is the point of
         calling this again. Going on would replay the entry: `enter` puts the panel back
         off the side of the screen with transitions off, and taking it away 20ms later
         slides it in a second time. That is the flash on a buy box add, where the cart
         is read once on the event and again when the count catches up. Re-focusing the
         close button would be its own interruption, so that goes with it. */
      if (!box.hidden) return;
      var slide = !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (slide) {
        box.classList.add('enter'); scrim.classList.add('enter');
        scrim.hidden = false; box.hidden = false;
        setTimeout(function () { box.classList.remove('enter'); scrim.classList.remove('enter'); }, 20);
      } else {
        box.classList.remove('enter'); scrim.classList.remove('enter');
        scrim.hidden = false; box.hidden = false;
      }
      document.documentElement.style.overflow = 'hidden';
      var x = q('[data-ac-close]'); if (x) x.focus();
    }
    return { open: open, post: post, refresh: refresh,
             setFilter: function (k) { lastKey = k; } };
  }

  document.querySelectorAll('[data-jump]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      var t = document.querySelector(a.getAttribute('href'));
      if (!t) return;
      e.preventDefault();
      var smooth = !document.hidden
        && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      t.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      var live = t.querySelector('[data-sb]:not([hidden]) .brand, [data-sb]:not([hidden]) .fit');
      if (live) setTimeout(function () { live.focus({ preventScroll: true }); }, 420);
    });
  });

  document.querySelectorAll('[data-cp][data-kind="bundle"]').forEach(function (sec) {
    var node = sec.querySelector('[data-cp-data]');
    var D;
    try { D = JSON.parse(node.textContent); } catch (e) { return; }
    var F = D.filters || {};

    /* ---- the parts, which arrive after the page ----------------------------
       The page ships the brands, the models and the diagrams, which is everything
       steps one and two ask for. The parts and the products behind them come from
       D.dataUrl once the page has painted, because resolving them is what made the
       page slow: 1.5 to 4.1 seconds uncached with them in the render against 1.0 to
       1.2 without, and 5.7 to 9.7 first thing in the morning. Visitors land here cold
       from an ad, so the cold number is the one that counts.

       Every filter starts with an empty parts list rather than none at all. Several
       readers filter over it, and an empty list gives them the answer they already
       know how to show, where a missing one throws. */
    Object.keys(F).forEach(function (h) { if (!F[h].parts) F[h].parts = []; });

    /* Each product is sent once and the parts point at it by variant id: 303 part
       entries between them name only 157 distinct products. Stitched here, once, so
       everything downstream goes on reading a part with a title and a price of its
       own and none of it has to know. */
    function absorb(d) {
      var prods = (d && d.products) || {};
      var byFilter = (d && d.parts) || {};
      Object.keys(byFilter).forEach(function (h) {
        if (!F[h]) return;
        F[h].parts = (byFilter[h] || []).map(function (p) {
          var pr = p.pv != null && prods[p.pv];
          if (pr) for (var k in pr) if (!(k in p)) p[k] = pr[k];
          return p;
        });
      });
    }

    var partsState = 'idle', partsWaiting = [];
    /* An older payload carried the parts inline. Take them if they are there, so a
       cached page from before this change still works. */
    if (D.products || D.parts) { absorb(D); partsState = 'ready'; }

    function settleParts(ok) {
      var queue = partsWaiting;
      partsWaiting = [];
      queue.forEach(function (cb) { cb(ok); });
    }
    /* Failure leaves the state at idle rather than at a third state of its own: the
       next model click retries, which is what someone who just saw "choose it again"
       is going to do. */
    function whenParts(cb) {
      if (partsState === 'ready') { if (cb) cb(true); return; }
      if (cb) partsWaiting.push(cb);
      if (partsState === 'loading' || !D.dataUrl) return;
      partsState = 'loading';
      /* No Accept header. Asking a product URL for application/json is answered by
         Shopify itself, with the product, before the template is ever reached: the
         first version of this got back a body_html and a variants array and quietly
         found no parts in it. The view parameter is what selects the template, and it
         only works on an ordinary page request. */
      fetch(D.dataUrl, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (d) { absorb(d); partsState = 'ready'; settleParts(true); })
        .catch(function () { partsState = 'idle'; settleParts(false); });
    }
    /* Start it now. Choosing a brand and then a model is two clicks and some reading,
       which is nearly always longer than the fetch, so the wait below rarely shows. */
    whenParts(null);
    var SUP = D.supplies || [];
    var L = D.labels || {};
    /* 3 is discontinued (aps.discontinued): no quantity and no button, because it cannot be
       bought and is not coming back, so Notify me would promise something (Dylan, 2026-10-11). */
    var STK = [['ok', L.inStock], ['low', L.lowStock], ['no', L.outOfStock], ['no', L.discontinued || 'Discontinued']];

    /* The marker is 26 square on the drawing PoolZoom serves, which is 767 wide.
       It is not a constant here: a diagram stored at a different size needs its
       markers at the same ratio, or every one of them lands short of its number by
       half the difference. de_filter.marker_size carries it per diagram, in the
       same pixels as the positions. */

    function qtyOf(el) {
      var b = el && el.querySelector('.qty2 b');
      return b ? Math.max(1, parseInt(b.textContent, 10) || 1) : 1;
    }

    /* One request for however many lines are ticked, and the modal takes it from
       there rather than the cart drawer. */
    function addToCart(items, btn, k) {
      if (!items.length) return;
      /* innerHTML, not textContent. The bundle button is a label and a price in two
         elements, and the price carries its cents in a <sup>. Read back as text and
         written back as text, the pair came back as one run with no styling and no
         decimal point: "Add bundle to cart$50146". */
      var label = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = L.adding;
      modal.post(items)
        .then(function () { return modal.refresh(); })
        .then(function () { modal.open(k); })
        .catch(function () {})
        .then(function () { btn.innerHTML = label; btn.disabled = false; });
    }

    var modal = makeCartModal(sec, D);

    /* The buy box add is Horizon's: its product form posts the cart, handles the
       variant and the quantity, and announces the result. We do not intercept it,
       we answer it. Our own adds announce the same event, so this skips those to
       avoid opening the modal twice over one click. */
    document.addEventListener('shopify:cart:lines-update', function (e) {
      var d = e.detail || {};
      if (d.source === 'aps-companion') return;
      if (d.action && d.action !== 'add') return;
      /* Horizon dispatches this while it is posting, not after. Read the cart on the
         event alone and it has not caught up: on a first add the panel said nought items
         over a cart that already had one. So the first read opens the panel and a few
         more follow it, stopping as soon as the count moves. */
      modal.refresh().then(function (first) {
        modal.open();
        var was = first && first.item_count;
        var tries = 0;
        (function again() {
          if (tries++ >= 4) return;
          setTimeout(function () {
            modal.refresh().then(function (c) {
              if (c && c.item_count !== was) modal.open();
              else again();
            });
          }, 300);
        }());
      });
    });

    var steps = [].slice.call(sec.querySelectorAll('[data-steps] li')),
        panels = [].slice.call(sec.querySelectorAll('[data-sb]')),
        brandBtns = [].slice.call(sec.querySelectorAll('.brand')),
        crumb = sec.querySelector('[data-brandcrumb]'),
        partFind = sec.querySelector('[data-part-find]'),
        pdcount = sec.querySelector('[data-pdcount]'),
        views = [].slice.call(sec.querySelectorAll('[data-sv]')),
        parts = sec.querySelector('[data-parts]'),
        picked = sec.querySelector('[data-picked]'),
        img = sec.querySelector('[data-diagram]'),
        cap = sec.querySelector('[data-dgcap]'),
        layer = sec.querySelector('.dgimg [data-spot-layer]'),
        rows = sec.querySelector('[data-rows]'),
        slot = sec.querySelector('[data-bundle-slot]'),
        find = sec.querySelector('[data-find-input]'),
        clr = sec.querySelector('[data-find-clear]'),
        empty = sec.querySelector('[data-find-empty]');
    var chips = [].slice.call(sec.querySelectorAll('.fit'));

    var resetZoom = makeZoom(sec.querySelector('.dgimg'));
    var openQV = makeQV(sec.closest('.aps-frame') || document.body);

    /* ---- the parts list ------------------------------------------------- */

    /* Howdy's .hb-pd-row.rich, structure for structure: an information block over a
       buy block. Five things on one 380px line is what made ours unreadable, and the
       fix is theirs rather than a smaller font. */
    function rowHTML(p, inb) {
      var st = STK[p.v] || STK[0];
      var small = [p.role, p.s].filter(Boolean).join(' \u00b7 ');
      var buy = p.p == null
        ? '<span class="pstk no">' + esc(L.notStocked) + '</span>'
        : p.v === 3 ? ''
        : (p.v === 2 ? '' : '<span class="qty2"><button type="button" aria-label="Less">&minus;</button>'
            + '<b>1</b><button type="button" aria-label="More">+</button></span>')
          + '<button type="button" class="obtn sm" data-add="' + p._i + '"'
          + (p.v === 2 ? ' disabled' : '') + '>'
          + esc(p.v === 2 ? L.notify : L.addToCart) + '</button>';
      return '<div class="prow' + (inb ? ' inb' : '') + '" data-cal="' + att(p.cal) + '" data-i="' + p._i + '">'
        + '<div class="rtop">'
        + '<span class="cal">' + esc(p.cal) + '</span>'
        + '<span class="pthumb">' + (p.img ? '<img alt="" loading="lazy" src="' + att(p.img) + '">' : '') + '</span>'
        + '<span class="pinfo">'
        + (small ? '<span class="psku">' + esc(small) + '</span>' : '')
        + '<span class="pname">' + esc(p.t) + '</span>'
        + (inb ? '<span class="inb-tag">In the bundle</span>' : '')
        + (p.p == null ? '' : '<span class="pprice">' + money(p.p) + '</span>')
        + (p.p == null ? '' : '<span class="pstk ' + st[0] + '">' + esc(st[1]) + '</span>')
        + '</span>'
        + '<button type="button" class="inf" data-vd="' + p._i + '" aria-label="'
        + att(L.viewDetails + ': ' + p.t) + '">i</button>'
        + '</div>'
        + '<div class="rbuy">' + buy + '</div></div>';
    }

    /* ---- the bundle, as this page's own frequently bought together row ----
       Class for class with aps-product-fbt: the grid set pinned on the left, its
       companions joined by plus signs, one subtotal and one button. It reads as the
       same component rather than a third thing that resembles it. */

    /* PoolZoom's parts finder mark, the same one the job kit's button carries.
       The drawing behind this button is what that button goes looking for, so the
       two say the same thing with the same glyph. */
    var TOOLS = '<svg class="pf" viewBox="0 0 24 24" aria-hidden="true">'
      + '<path fill="currentColor" d="M20 17.57C20.4954 16.7582 20.7078 15.805 20.6037 14.8596C20.4997 13.9142 20.0851 13.03 19.425 12.3454C18.7649 11.6607 17.8963 11.2142 16.9554 11.0758C16.0145 10.9374 15.0542 11.1148 14.2248 11.5803C13.3954 12.0458 12.7438 12.7732 12.3719 13.6485C12 14.5239 11.9288 15.4978 12.1694 16.418C12.4101 17.3381 12.949 18.1525 13.7018 18.7337C14.4546 19.3149 15.3789 19.6301 16.33 19.63C17.1199 19.6263 17.894 19.4086 18.57 19L20.29 20.73C20.383 20.8237 20.4936 20.8981 20.6154 20.9489C20.7373 20.9997 20.868 21.0258 21 21.0258C21.132 21.0258 21.2627 20.9997 21.3846 20.9489C21.5064 20.8981 21.617 20.8237 21.71 20.73C21.8037 20.637 21.8781 20.5264 21.9289 20.4046C21.9797 20.2827 22.0058 20.152 22.0058 20.02C22.0058 19.888 21.9797 19.7573 21.9289 19.6354C21.8781 19.5136 21.8037 19.403 21.71 19.31L20 17.57ZM18 17C17.5593 17.42 16.9738 17.6543 16.365 17.6543C15.7562 17.6543 15.1707 17.42 14.73 17C14.2983 16.5654 14.056 15.9776 14.056 15.365C14.056 14.7524 14.2983 14.1646 14.73 13.73C14.9446 13.515 15.1994 13.3445 15.48 13.2281C15.7605 13.1117 16.0613 13.0518 16.365 13.0518C16.6687 13.0518 16.9695 13.1117 17.25 13.2281C17.5306 13.3445 17.7854 13.515 18 13.73C18.4317 14.1646 18.674 14.7524 18.674 15.365C18.674 15.9776 18.4317 16.5654 18 17ZM19 3H5C4.20435 3 3.44129 3.31607 2.87868 3.87868C2.31607 4.44129 2 5.20435 2 6V15C2 15.7956 2.31607 16.5587 2.87868 17.1213C3.44129 17.6839 4.20435 18 5 18H9C9.26522 18 9.51957 17.8946 9.70711 17.7071C9.89464 17.5196 10 17.2652 10 17C10 16.7348 9.89464 16.4804 9.70711 16.2929C9.51957 16.1054 9.26522 16 9 16H5C4.73478 16 4.48043 15.8946 4.29289 15.7071C4.10536 15.5196 4 15.2652 4 15V9H20V10C20 10.2652 20.1054 10.5196 20.2929 10.7071C20.4804 10.8946 20.7348 11 21 11C21.2652 11 21.5196 10.8946 21.7071 10.7071C21.8946 10.5196 22 10.2652 22 10V6C22 5.20435 21.6839 4.44129 21.1213 3.87868C20.5587 3.31607 19.7956 3 19 3ZM20 7H4V6C4 5.73478 4.10536 5.48043 4.29289 5.29289C4.48043 5.10536 4.73478 5 5 5H19C19.2652 5 19.5196 5.10536 19.7071 5.29289C19.8946 5.48043 20 5.73478 20 6V7ZM10 11H7C6.73478 11 6.48043 11.1054 6.29289 11.2929C6.10536 11.4804 6 11.7348 6 12C6 12.2652 6.10536 12.5196 6.29289 12.7071C6.48043 12.8946 6.73478 13 7 13H10C10.2652 13 10.5196 12.8946 10.7071 12.7071C10.8946 12.5196 11 12.2652 11 12C11 11.7348 10.8946 11.4804 10.7071 11.2929C10.5196 11.1054 10.2652 11 10 11Z"/></svg>';

    /* the same truck the cart modal draws, so the promise looks the same in both */
    var TRUCK = '<svg viewBox="0 0 24 24" aria-hidden="true">'
      + '<path d="M2.6 6.4h10.9v9.4H2.6z"/><path d="M13.5 9.4h3.7l3.3 3.4v3h-7z"/>'
      + '<circle cx="6.9" cy="18.1" r="1.9"/><circle cx="17.2" cy="18.1" r="1.9"/></svg>';

    function partsBtn(f) {
      return '<button type="button" class="obtn ghost" data-sv-go="parts">' + TOOLS
        + esc(L.seeParts || 'View full parts list & diagram')
        + '<span class="cnt">' + f.parts.length + '</span></button>';
    }

    /* Why a grid set is not the whole job. The copy is a setting so merchandising can
       change it, and its default is section 5 of the plan, which read the threads
       people arrive from: the trigger is DE in the pool, torn grids are usually the
       cause and almost never the only one.
       The bubble is a sibling of the heading, never inside it: a tooltip inside an
       <h3> becomes part of the heading every screen reader announces. */
    function whyBtn() {
      var why = L.bundleWhy || '';
      if (!why) return '';
      return '<span class="ihwrap"><button type="button" class="ihint" data-why'
        + ' aria-expanded="false" aria-label="Why these parts">'
        + '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/>'
        + '<path d="M12 11v5"/><circle cx="12" cy="7.6" r=".9" fill="currentColor" stroke="none"/>'
        + '</svg></button><span class="ihbub" role="tooltip">'
        + why.split(/\n\s*\n/).map(function (p) { return '<span>' + esc(p.trim()) + '</span>'; }).join('')
        + '</span></span>';
    }

    /* Which of this brand's filter models a given grid size fits. The area is written
       into the model number, so DE3620 and DE3600 are both the 36, and reading it back
       out beats keeping a second table in step with the first. Tried largest first, or
       DE7220 would answer to the 20 in its tail. */
    var AREAS = [24, 36, 48, 60, 72];
    function areaOf(text) {
      /* The first run of digits, and its leading pair. Searching for the area as a
         substring anywhere in the string put DE3600 under the 60, because the 6 and
         the first 0 sit next to each other. The number a model carries starts where
         its letters stop. */
      var m = String(text).match(/\d+/);
      if (!m) return null;
      var n = parseInt(m[0].slice(0, 2), 10);
      return AREAS.indexOf(n) > -1 ? n : null;
    }
    function modelsFor(brand, variantTitle) {
      var want = areaOf(variantTitle), out = [];
      if (!want) return '';
      Object.keys(F).forEach(function (k) {
        if (F[k].brand !== brand) return;
        String(F[k].models || '').split(',').forEach(function (m) {
          m = m.trim();
          if (m && areaOf(m) === want && out.indexOf(m) < 0) out.push(m);
        });
      });
      return out.join(', ');
    }
    function sizeSelect(f, hero) {
      var vs = (hero && hero.variants) || [];
      if (vs.length < 2) return '';
      return '<select class="vsel" aria-label="Grid size">'
        + vs.map(function (v) {
            var models = modelsFor(f.brand, v.t);
            var size = String(v.t).split(' (')[0];
            return '<option value="' + v.id + '"' + (v.id === hero.vid ? ' selected' : '')
              + (v.a ? '' : ' disabled') + '>'
              + esc(size + (models ? ' (' + models + ')' : '')) + '</option>';
          }).join('')
        + '</select>';
    }

    function bundleHTML(f) {
      /* By handle, not callout: a diagram can number two different parts the same,
         and resolving on the number returned whichever came first. */
      var items = f.bundle.map(function (h) {
        return f.parts.filter(function (p) { return p.h === h && p.p != null; })[0];
      }).filter(Boolean);
      if (!items.length) {
        return '<div class="bcard pending">'
          + '<div class="bbar"><span class="bid"><span class="bk">Bundle</span></span>'
          + '<span class="facts">' + partsBtn(f) + '</span></div>'
          + '<h3>Not picked for this filter yet</h3>'
          + '<p class="bsub">The parts list has this filter\u2019s own parts, straight from '
          + 'its diagram. Which of them belong in a bundle is a merchandising call.</p></div>';
      }
      /* The current item is this product at the size the buy box has selected, not
         whichever grid set the diagram happens to call out. The bundle's own grid set
         is dropped so the row does not sell two of them. */
      var hero = D.hero || null;
      if (hero) items = items.filter(function (p) { return p.role !== 'Grid set'; });
      var all = (hero ? [{ vid: hero.vid, u: hero.u, p: hero.p, cmp: hero.cmp,
                           img: hero.img, t: hero.t, s: hero.s, v: hero.v,
                           role: hero.vt && hero.vt !== 'Default Title' ? hero.vt : '' }] : [])
        .concat(items).concat(D.supplies || []);
      f._items = all;
      function card(o, first, i) {
        return '<div class="fbt-i' + (first ? ' this' : '') + '" data-bi="' + i + '"'
          + ' data-vid="' + o.vid + '"'
          + ' data-u="' + att(o.u || '') + '" data-p="' + o.p + '"'
          + ' data-reg="' + (o.cmp > o.p ? o.cmp : o.p) + '" data-img="' + att(o.img || '') + '">'
          + '<label class="sel"><input type="checkbox" checked>'
          + '<span class="cbx" aria-hidden="true"></span>'
          + (first ? '<span class="cur">Current item</span>' : ' Selected') + '</label>'
          + '<span class="im2">' + (o.img ? '<img alt="" loading="lazy" src="' + att(o.img) + '">' : '') + '</span>'
          + (first && hero ? sizeSelect(f, hero)
             : (o.role ? '<div class="br2 role">' + esc(o.role) + '</div>' : ''))
          + '<h4>' + esc(o.t) + '</h4>'
          + '<div class="pp2">' + money(o.p) + '</div></div>';
      }
      /* One bar, at the top, pinned there: what the bundle is, what it costs, what it
         saves and both ways out. A head above the products and a foot below them put
         the price off the bottom of the screen on a row two deep. */
      return '<div class="bcard">'
        + '<div class="bbar">'
        + '<span class="bid"><span class="bk">Complete bundle</span>'
        + '<span class="bsave"></span></span>'
        + '<span class="bship" data-bship hidden></span>'
        + '<span class="facts">'
        + '<button type="button" class="obtn big">'
        + '<span class="blab">' + esc(L.bundleAdd) + '</span>'
        + '<b class="bnow"></b></button></span>'
        + '</div>'
        + '<div class="bhx"><h3>Everything a grid change needs</h3>' + whyBtn() + '</div>'
        + '<p class="bsub"><span data-bsel></span></p>'
        + '<div class="fbt-row">'
        + all.map(function (o, i) { return card(o, i === 0, i); }).join('')
        + '</div>'
        + '<div class="bfoot">' + partsBtn(f) + '</div></div>';
    }

    /* Every card starts ticked, because the bundle is the recommendation. Untick one
       and the price, the saving and the button have to agree with what is left. */
    function wireBundle(card, key) {
      if (!card || card.classList.contains('pending')) return;
      var cards = [].slice.call(card.querySelectorAll('.fbt-i')),
          now = card.querySelector('.bnow'), lab = card.querySelector('.blab'),
          save = card.querySelector('.bsave'), sel = card.querySelector('[data-bsel]'),
          ship = card.querySelector('[data-bship]'),
          btn = card.querySelector('.obtn.big'), all = cards.length;
      function upd() {
        var s = 0, r = 0, n = 0;
        cards.forEach(function (c) {
          var box = c.querySelector('input');
          c.classList.toggle('off', !box.checked);
          if (!box.checked) return;
          n++; s += +c.dataset.p; r += +c.dataset.reg;
        });
        now.innerHTML = money(s);
        var cut = r - s;
        save.textContent = cut > 0 ? 'Save ' + flat(cut) : ''; save.hidden = cut <= 0;
        sel.textContent = n + ' of ' + all + ' items selected';
        btn.disabled = !n;
        /* The label is its own element now, because the price sits beside it inside the
           button and writing textContent on the button would take the price with it. */
        lab.textContent = !n ? L.bundleNone
          : (n === all ? L.bundleAdd : 'Add ' + n + (n === 1 ? ' item' : ' items') + ' to cart');
        now.hidden = !n;
        /* Read off what is ticked, not off the filter: taking two parts out of the
           bundle can drop the order back under the threshold. */
        var thr = D.freeShip || 0;
        if (ship) {
          ship.hidden = !(thr && n);
          if (thr && n) {
            var over = s >= thr;
            ship.classList.toggle('on', over);
            ship.innerHTML = TRUCK + (over ? '<span><b>Free shipping</b></span>'
              : '<span><b>' + flat(thr - s) + '</b> to <b>free shipping</b></span>');
          }
        }
      }
      cards.forEach(function (c) { c.querySelector('input').addEventListener('change', upd); });
      btn.addEventListener('click', function () {
        addToCart(cards.filter(function (c) { return c.querySelector('input').checked; })
          .map(function (c) { return { id: +c.dataset.vid, quantity: 1 }; }), btn, key);
      });
      upd();
    }


    /* ---- the diagram ----------------------------------------------------- */

    var chosen = null;
    function paint(k) {
      var f = F[k];
      if (!f) return;
      chosen = k;
      resetZoom();
      img.src = f.dg;
      img.alt = f.brand + ' ' + f.name + ', exploded parts diagram';
      /* Bound the stage by this drawing's own aspect. The pins are a percentage of
         the stage, so shrinking the picture inside it would drift every one of them. */
      var stage = sec.querySelector('.pdstage');
      if (stage) {
        stage.style.maxWidth = 'min(560px, calc(62vh * ' + (f.nw / f.nh).toFixed(4) + '))';
        /* Reserve the height before the drawing arrives. The parts view starts hidden,
           so its image has usually not decoded when it is first revealed: without this
           the stage is a zero-height box at the moment we scroll, and the picture then
           pushes the page down under the reader, which is how you end up looking at the
           bottom of a diagram you just asked to see. */
        stage.style.aspectRatio = f.nw + ' / ' + f.nh;
      }
      cap.textContent = f.brand + ' ' + f.name + ', exploded parts diagram';
      f.parts.forEach(function (p, i) { p._i = i; });
      slot.innerHTML = bundleHTML(f);
      wireBundle(slot.querySelector('.bcard'), k);
      rows.innerHTML = f.parts.map(function (p) {
        return rowHTML(p, f.bundle.indexOf(p.h) > -1);
      }).join('');

      /* Every callout the drawing numbers gets a pin, and the pin carries the number.
         It is placed by its centre: the percentage is the middle of the circle and the
         transform does the rest, so how large it is drawn no longer depends on which
         pixel space the positions happen to be stored in. That coupling put every pin
         half a pin up and left of its number, twice. */
      var spot = f.spot || 26;
      layer.innerHTML = '';
      var pins = [];
      f.parts.forEach(function (p, pi) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hs';
        b.dataset.cal = p.cal;
        b.dataset.i = pi;
        b.textContent = p.cal;
        b.setAttribute('aria-label', 'Show ' + p.t + ', part ' + p.cal + ' on this diagram');
        b.style.left = ((p.x + (p.ox || 0) + spot / 2) / f.nw * 100) + '%';
        b.style.top = ((p.y + (p.oy || 0) + spot / 2) / f.nh * 100) + '%';
        layer.appendChild(b);
        pins.push(b);
      });

      /* Pin and row are two views of one part, so lighting either lights both, and
         the list brings the row to where the eye already is. */
      function select(pi) {
        pins.forEach(function (b) { b.classList.toggle('on', +b.dataset.i === pi); });
        var hit = null;
        rows.querySelectorAll('.prow').forEach(function (r) {
          var on = +r.dataset.i === pi;
          r.classList.toggle('on', on);
          if (on) hit = r;
        });
        if (hit) {
          var lb = rows.getBoundingClientRect(), rb = hit.getBoundingClientRect();
          rows.scrollTo({
            top: Math.max(0, rows.scrollTop + (rb.top - lb.top) - 58),
            // a hidden tab freezes the frame clock, and a smooth scroll that never
            // runs leaves the row off screen
            behavior: (document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches)
              ? 'auto' : 'smooth'
          });
        }
      }
      pins.forEach(function (b) {
        /* A pin lights its row and opens the part, the same as clicking the row. It
           still selects first, so the list scrolls to the row underneath and the
           drawer is not the only way back to where you were. */
        b.addEventListener('click', function () {
          var pi = +b.dataset.i;
          select(pi);
          openQV(f, f.parts[pi], b);
        });
      });
      rows.querySelectorAll('.prow').forEach(function (r) {
        r.addEventListener('click', function (e) {
          if (e.target.closest('.obtn, .qty2, .vdet')) return;   // buying is not selecting
          select(+r.dataset.i);
        });
      });

      /* the count above the list, and the search that narrows it */
      var total = rows.querySelectorAll('.prow').length;
      function tally(n) {
        if (pdcount) pdcount.textContent = (n === total ? total + ' parts' : n + ' of ' + total + ' parts');
      }
      tally(total);
      if (partFind) {
        partFind.value = '';
        partFind.oninput = function () {
          var q = this.value.trim().toLowerCase(), n = 0;
          rows.querySelectorAll('.prow').forEach(function (r) {
            var hit = !q || r.textContent.toLowerCase().indexOf(q) > -1;
            r.hidden = !hit;
            if (hit) n++;
          });
          tally(n);
        };
      }

      rows.querySelectorAll('.qty2').forEach(stepper);
      rows.querySelectorAll('[data-vd]').forEach(function (b) {
        b.addEventListener('click', function () { openQV(f, f.parts[+b.dataset.vd], b); });
      });
      rows.querySelectorAll('[data-add]').forEach(function (b) {
        b.addEventListener('click', function () {
          var p = f.parts[+b.dataset.add];
          addToCart([{ id: p.vid, quantity: qtyOf(b.closest('.pbuy')) }], b, k);
        });
      });
    }

    /* ---- picking a filter ------------------------------------------------ */

    /* The bundle and the drawing are two faces of the same step, not two steps.
       Switching between them leaves the chosen filter alone. */
    /* The chosen filter bar is the only chrome above the drawing, so the way back
       lives on it rather than in a row of its own. That row was the only thing in the
       gap under the bar, and the gap was the blue band. */
    function showView(name, scroll) {
      views.forEach(function (v) { v.hidden = v.dataset.sv !== name; });
      var c = sec.querySelector('[data-change]');
      if (c) {
        var onParts = name === 'parts';
        c.textContent = onParts ? (L.backToBundle || 'Back to the bundle')
                                : (L.changeFilter || 'Change filter');
        c.dataset.back = onParts ? '1' : '';
      }
      /* Switching swaps a tall panel for a different one under a reader who is
         usually at the bottom of the first, so without this you arrive at the bottom
         of the drawing. Anchored on the chosen filter bar, the view starts where it
         reads from. Smooth only when the tab is visible: a hidden tab freezes the
         frame clock and a smooth scroll never lands. */
      if (scroll) {
        /* scrollIntoView rather than a computed window.scrollTo, and instant rather
           than smooth. A view switch is not a scroll gesture: a smooth run from the
           foot of a long bundle can be cancelled by anything that scrolls after it,
           and when it is cancelled you are left wherever you were, which is the
           bottom of the drawing. The offset is scroll-margin-top on the bar. */
        requestAnimationFrame(function () {
          (sec.querySelector('.picked') || sec).scrollIntoView({ block: 'start' });
        });
      }
    }
    /* The whole card and the whole row open the drawer, not one small target inside
       them. Everything that already does something on its own is excluded by name:
       the tick, the stepper, add to cart, the diagram link. Delegated on the section
       because both are rebuilt on every filter. */
    var HANDS_OFF = 'input, label, button, a, select, .qty2, [data-add], [data-sv-go]';
    sec.addEventListener('click', function (e) {
      if (e.target.closest(HANDS_OFF)) return;
      var f = chosen && F[chosen];
      if (!f) return;
      var card = e.target.closest('.fbt-i[data-bi]');
      if (card && sec.contains(card)) {
        var it = (f._items || [])[+card.dataset.bi];
        if (it) openQV(f, it, card);
        return;
      }
      var row = e.target.closest('.pdlist .prow');
      if (row && sec.contains(row)) {
        var p = f.parts[+row.dataset.i];
        if (p) openQV(f, p, row);
      }
    });

    /* The size chips are aps-pdp.js's, in another section, and it announces the move
       rather than making every listener poll. The bundle's first card is this product,
       so it has to follow. Rebuilding the row is cheaper than reaching into one card,
       and it keeps the subtotal honest. */
    /* Changing the size here changes it in the buy box, rather than keeping a second
       idea of the selected variant. The chip click runs aps-pdp.js's own selectVariant,
       which announces aps:variant, which rebuilds this row. One source of truth. */
    sec.addEventListener('change', function (e) {
      var sel = e.target.closest('.vsel');
      if (!sel || !sec.contains(sel)) return;
      var chip = document.querySelector('.sizes .opt[data-vid="' + sel.value + '"]');
      if (chip) chip.click();
    });

    document.addEventListener('aps:variant', function (e) {
      var v = e.detail;
      if (!v || !D.hero || !chosen || !F[chosen]) return;
      D.hero.vid = v.id;
      D.hero.p = v.price;
      D.hero.cmp = v.compare || 0;
      D.hero.vt = v.title;
      D.hero.s = v.sku || D.hero.s;
      if (v.image) D.hero.img = v.image;
      D.hero.v = D.hero.v === 3 ? 3 : (v.available === false ? 2 : 0);
      slot.innerHTML = bundleHTML(F[chosen]);
      wireBundle(slot.querySelector('.bcard'), chosen);
    });

    /* Delegated: the button that opens the drawing is rebuilt with the bundle on
       every paint, so a listener bound once at start-up would only catch the back
       link that was in the markup. */
    sec.addEventListener('click', function (e) {
      var b = e.target.closest('[data-sv-go]');
      if (b && sec.contains(b)) showView(b.dataset.svGo, true);
    });

    /* The tooltip opens on click as well as hover, because a phone has no hover and
       the answer to "why am I being sold five things" should not be desktop only.
       Bound on document rather than the section: a click that lands outside the
       section has to close it, and a listener on the section never hears one. */
    function shutWhy(except) {
      sec.querySelectorAll('[data-why]').forEach(function (b) {
        if (b === except) return;
        b.setAttribute('aria-expanded', 'false');
        var bub = b.parentNode.querySelector('.ihbub');
        if (bub) bub.classList.remove('on');
      });
    }
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-why]');
      if (b && sec.contains(b)) {
        var open = b.getAttribute('aria-expanded') === 'true';
        shutWhy(b);
        b.setAttribute('aria-expanded', String(!open));
        b.parentNode.querySelector('.ihbub').classList.toggle('on', !open);
        return;
      }
      if (!e.target.closest('.ihbub')) shutWhy();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') shutWhy(); });

    /* One panel visible at a time, and the numbers say where you are. Going back a
       step leaves the later ones untouched, so changing brand does not silently keep a
       model belonging to the brand just left. */

    /* The picture spans from the top of the heading to the top of the brand tiles.
       Nothing in the flow covers that: the heading is a child of the section and the
       tiles are inside step one. So it is positioned against the section, and hidden
       here on the steps where step one is not the one showing. */
    var art = sec.querySelector('.cpart');

    /* The film, muted, on a loop, started when the block is scrolled to.

       It was a YouTube embed and is a file we serve. YouTube draws its own furniture
       inside that iframe, a More videos grid over the frame the moment anything pauses,
       and an iframe is another document: none of it can be reached from here. Its mute
       could not be worked either, since commands travel by postMessage and are dropped
       until the player announces itself, which needs a handshake this was not doing.
       A <video> has none of that, and none of the megabyte of script.

       Still an observer rather than a load. preload is none, so the file is not fetched
       until play is called, which keeps a page reached cold from an ad paying nothing
       for a panel nobody has reached. On a phone it costs nothing at all: the box is
       hidden there and this never runs. */
    var media = art && art.hasAttribute('data-cp-media') ? art : null;
    var vid = media && media.querySelector('.cpvideo');
    var vidPausedByUser = false;
    var stillMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function markState() {
      if (!vid) return;
      media.classList.toggle('is-paused', vid.paused);
      media.classList.toggle('is-muted', vid.muted);
    }
    /* The loop is here rather than on the element, so the start offset holds on every
       pass. With the loop attribute set the video never ends, ended never fires, and
       every pass after the first would begin at nought.

       The offset is ignored when the file is too short for it, which is the difference
       between skipping a title card and a clip stuck at a time it does not have.

       Declared out here beside the other two rather than inside the guard below, because
       stopVideo calls it and a function declared in a block is not reliably visible
       outside one. */
    function seekStart() {
      if (!vid) return;
      var st = +media.getAttribute('data-cp-start') || 0;
      if (st > 0 && isFinite(vid.duration) && st < vid.duration - 0.5) {
        try { vid.currentTime = st; } catch (e) { /* not seekable yet */ }
      }
    }
    function playVideo() {
      if (!vid) return;
      /* play() rejects when the browser will not autoplay. Nothing to do about it and
         nothing to report: the control is already showing, because paused is the state
         the class reflects. */
      var p = vid.play();
      if (p && p.catch) p.catch(function () { markState(); });
    }
    /* Leaving step one is a stop rather than a pause: the box is gone by then, and a
       hidden video that is still playing is sound from a part of the page nobody is
       looking at. */
    function stopVideo() {
      if (!vid) return;
      vid.pause();
      seekStart();
      vidPausedByUser = false;
      markState();
    }
    if (vid) {
      vid.muted = true;
      vid.addEventListener('loadedmetadata', seekStart);
      vid.addEventListener('ended', function () { seekStart(); playVideo(); });
      vid.addEventListener('play', markState);
      vid.addEventListener('pause', markState);
      vid.addEventListener('volumechange', markState);

      var toggle = media.querySelector('[data-cp-toggle]');
      if (toggle) toggle.addEventListener('click', function () {
        vidPausedByUser = !vid.paused;
        if (vid.paused) playVideo(); else vid.pause();
      });

      var sound = media.querySelector('[data-cp-sound]');
      if (sound) sound.addEventListener('click', function () {
        vid.muted = !vid.muted;
        /* Unmuting something that is not running is a request to hear it. */
        if (!vid.muted && vid.paused) { vidPausedByUser = false; playVideo(); }
        markState();
      });

      /* Off screen it pauses and keeps its place, so scrolling back carries on rather
         than starting the file again. */
      if (window.IntersectionObserver) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              if (!vidPausedByUser && !(stillMotion && vid.currentTime === 0)) playVideo();
            } else if (!vid.paused) {
              vid.pause();
            }
          });
        }, { threshold: 0.35 }).observe(media);
      } else if (!stillMotion) {
        playVideo();
      }
      markState();
    }

    /* ---- the same panel, played through YouTube ---------------------------
       Only when there is no file. A YouTube link added to Shopify Files is stored as a
       reference and not a copy: the record is an ExternalVideo whose origin is on
       youtube.com, so there is nothing on Shopify's CDN to serve.

       The complaint that started this was YouTube's own furniture, and the answer is not
       to argue with the iframe but to deny it the state that draws it. The More videos
       grid appears when the player pauses, so pausing here takes the player away and
       puts the still back. Nothing to draw over. Starting again rebuilds it at the
       offset, which for an ambient loop is close enough to where it was.

       Sound goes through postMessage, and postMessage to an embed is ignored until the
       player has been told someone is listening. That handshake is what was missing when
       the unmute did nothing. */
    var ytHost = art && art.getAttribute('data-cp-yt') ? art : null;
    var ytFrame = null, ytPausedByUser = false;

    function ytSend(msg) {
      if (ytFrame && ytFrame.contentWindow) {
        ytFrame.contentWindow.postMessage(JSON.stringify(msg), '*');
      }
    }
    function ytCmd(func) {
      /* The handshake goes again before each command rather than once on load. It is a
         message into a frame that may have been rebuilt or may not have finished, and
         sending it twice costs nothing. */
      ytSend({ event: 'listening' });
      ytSend({ event: 'command', func: func, args: [] });
    }
    function ytStart() {
      if (!ytHost || ytFrame) return;
      var id = ytHost.getAttribute('data-cp-yt');
      var st = +ytHost.getAttribute('data-cp-start') || 0;
      var f = document.createElement('iframe');
      f.className = 'cpframe';
      f.title = ytHost.querySelector('[data-cp-toggle]')
        ? ytHost.querySelector('[data-cp-toggle]').getAttribute('aria-label') : '';
      f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id)
        + '?autoplay=1&mute=1&loop=1&playlist=' + encodeURIComponent(id)
        + (st > 0 ? '&start=' + st : '')
        + '&controls=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1'
        + '&cc_load_policy=0&iv_load_policy=3&disablekb=1&fs=0'
        + '&origin=' + encodeURIComponent(location.origin);
      f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      f.setAttribute('tabindex', '-1');
      f.addEventListener('load', function () { ytSend({ event: 'listening' }); });
      ytFrame = f;
      ytHost.querySelector('.cpstage').appendChild(f);
      ytHost.classList.add('is-live', 'is-muted');
      ytHost.classList.remove('is-paused');
    }
    function ytTeardown(paused) {
      if (!ytFrame) return;
      ytFrame.parentNode.removeChild(ytFrame);
      ytFrame = null;
      ytHost.classList.remove('is-live');
      ytHost.classList.add('is-muted');
      ytHost.classList.toggle('is-paused', !!paused);
    }
    if (ytHost) {
      var ytToggle = ytHost.querySelector('[data-cp-toggle]');
      if (ytToggle) ytToggle.addEventListener('click', function () {
        if (ytFrame) { ytPausedByUser = true; ytTeardown(true); }
        else { ytPausedByUser = false; ytStart(); }
      });
      var ytSound = ytHost.querySelector('[data-cp-sound]');
      if (ytSound) ytSound.addEventListener('click', function () {
        if (!ytFrame) { ytPausedByUser = false; ytStart(); return; }
        var muted = ytHost.classList.toggle('is-muted');
        ytCmd(muted ? 'mute' : 'unMute');
      });
      if (window.IntersectionObserver) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              if (!ytPausedByUser && !stillMotion) ytStart();
            } else {
              /* Out of view is not a pause the reader asked for, so it does not set the
                 paused state: scrolling back starts it again. */
              ytTeardown(false);
            }
          });
        }, { threshold: 0.35 }).observe(ytHost);
      } else if (!stillMotion) {
        ytStart();
      }
    }

    /* The picture's size is CSS. All that is left is to show it on step one only: it
       belongs to the brand question, and on the steps after it there is a bundle or a
       diagram in that space. */
    function sizeArt() {
      if (!art) return;
      var one = sec.querySelector('[data-sb="1"]');
      var show = one && !one.hidden;
      art.style.display = show ? '' : 'none';
      if (!show) { stopVideo(); ytTeardown(false); ytPausedByUser = false; }
    }
    /* No measuring left, so nothing to wait for: the picture can arrive whenever it
       likes. This waited on the image's load event back when the size was derived from
       it, and there is nothing to derive now. */
    requestAnimationFrame(sizeArt);
    function showStep(n) {
      panels.forEach(function (p) { p.hidden = +p.dataset.sb !== n; });
      /* Directly, not on the next frame. This waited for one back when it measured
         something that had to be laid out first, and it measures nothing now. It also
         stops the video, and a frame is not a safe delay for that: an animation frame in
         a background tab can be a long way off, which is where sound from a block nobody
         is looking at comes from. */
      sizeArt();
      steps.forEach(function (li) {
        var i = +li.dataset.st;
        li.classList.toggle('on', i === n);
        li.classList.toggle('done', i < n);
      });
    }
    function pickBrand(b) {
      brandBtns.forEach(function (x) {
        x.setAttribute('aria-pressed', String(x.dataset.brand === b));
      });
      /* The brand comes back as the mark they recognised on the tank, not as its
         name set in the body face. */
      if (crumb) {
        var lg = '';
        for (var k in F) { if (F[k].brand === b && F[k].logo) { lg = F[k].logo; break; } }
        crumb.innerHTML = lg ? '<img alt="' + att(b) + '" src="' + att(lg) + '">' : esc(b);
        crumb.classList.toggle('haslogo', !!lg);
      }
      // choosing a brand filters cards already on the page rather than fetching
      chips.forEach(function (c) {
        c.dataset.off = c.dataset.brand === b ? '' : '1';
        c.setAttribute('aria-pressed', 'false');
      });
      if (find) find.value = '';
      sift();
      showStep(2);
    }
    brandBtns.forEach(function (x) {
      x.addEventListener('click', function () { pickBrand(x.dataset.brand); });
    });
    sec.querySelectorAll('[data-back]').forEach(function (b) {
      b.addEventListener('click', function () { showStep(+b.dataset.back); });
    });

    var partsErr = sec.querySelector('[data-parts-err]');
    chips.forEach(function (c) {
      c.addEventListener('click', function () {
        var k = c.dataset.k, f = F[k];
        if (!f) return;
        chips.forEach(function (o) { o.setAttribute('aria-pressed', String(o === c)); });
        /* Step three is not shown until its parts are in hand. Revealing it first and
           filling it in afterwards would mean an empty bundle and a diagram with no
           pins on screen, which reads as the answer rather than as a wait. */
        if (partsErr) partsErr.hidden = true;
        c.setAttribute('aria-busy', 'true');
        whenParts(function (ok) {
          c.removeAttribute('aria-busy');
          if (!ok) {
            if (partsErr) partsErr.hidden = false;
            c.setAttribute('aria-pressed', 'false');
            return;
          }
          picked.hidden = false;
          picked.querySelector('[data-pkname]').textContent = f.brand + ' ' + f.name;
          picked.querySelector('[data-pkmodels]').textContent = f.models || '';
          paint(k);
          showView('bundle');            // the short answer first
          showStep(3);
          modal.setFilter(k);          // from here on we know their filter
        });
      });
    });

    var chg = sec.querySelector('[data-change]');
    if (chg) {
      chg.addEventListener('click', function () {
        if (chg.dataset.back) { showView('bundle', true); return; }
        chips.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
        if (find) { find.value = ''; sift(); }
        showStep(2);
      });
    }

    /* Somebody holding their tank label wants the model number, not the brand. */
    function sift() {
      if (!find) return;
      var q = find.value.trim().toLowerCase();
      var hits = 0;
      chips.forEach(function (c) {
        // the search only ever looks inside the brand already chosen
        var mine = c.dataset.off !== '1';
        var on = mine && (!q || c.dataset.find.indexOf(q) > -1);
        c.hidden = !on;
        if (on) hits++;
      });
      if (clr) clr.hidden = !q;
      if (empty) empty.hidden = hits > 0;
    }
    if (find) find.addEventListener('input', sift);
    if (clr) clr.addEventListener('click', function () { find.value = ''; sift(); find.focus(); });

    /* ---- pinch and pan on the diagram ------------------------------------
     * touch-action is pan-y until something is zoomed, so a finger dragged up the page
     * still scrolls it and only a second finger is ours; once zoomed it goes to none
     * and the single finger pans the drawing instead. The markers live inside the
     * element being transformed, so they scale with the picture and stay on their
     * callouts.
     */
    function makeZoom(dgimg) {
      var vp = document.createElement('span');
      vp.className = 'dgvp';
      dgimg.parentNode.insertBefore(vp, dgimg);
      vp.appendChild(dgimg);
      var ui = document.createElement('span');
      ui.className = 'dgz';
      ui.innerHTML = '<button type="button" data-z="-1" aria-label="Zoom out">&minus;</button>'
        + '<span class="lvl">100%</span>'
        + '<button type="button" data-z="1" aria-label="Zoom in">+</button>';
      vp.appendChild(ui);
      var out = ui.querySelector('[data-z="-1"]'), inb = ui.querySelector('[data-z="1"]'),
          lvl = ui.querySelector('.lvl');
      var MIN = 1, MAX = 4, k = 1, tx = 0, ty = 0;

      function clamp() {
        var w = vp.clientWidth, hh = vp.clientHeight;
        // at 1 the picture exactly fills the frame, so there is nothing to pan to
        tx = Math.min(0, Math.max(w - w * k, tx));
        ty = Math.min(0, Math.max(hh - hh * k, ty));
      }
      function apply() {
        clamp();
        dgimg.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + k + ')';
        vp.classList.toggle('zoomed', k > 1.001);
        lvl.textContent = Math.round(k * 100) + '%';
        out.disabled = k <= MIN + 0.001; inb.disabled = k >= MAX - 0.001;
      }
      // zoom about a point, so what is under the fingers stays under them
      function zoomTo(nk, px, py) {
        nk = Math.min(MAX, Math.max(MIN, nk));
        var cx = (px - tx) / k, cy = (py - ty) / k;
        k = nk; tx = px - cx * k; ty = py - cy * k; apply();
      }
      function reset() { k = 1; tx = 0; ty = 0; apply(); }
      function center(d) { zoomTo(k * d, vp.clientWidth / 2, vp.clientHeight / 2); }
      out.addEventListener('click', function () { center(1 / 1.6); });
      inb.addEventListener('click', function () { center(1.6); });

      function local(t) {
        var r = vp.getBoundingClientRect();
        return { x: t.clientX - r.left, y: t.clientY - r.top };
      }
      var d0 = 0, k0 = 1, mid = null, pan = null, moved = 0, lastTap = 0;

      vp.addEventListener('touchstart', function (e) {
        if (!vp.classList.contains('zoomable')) return;
        if (e.touches.length === 2) {
          var a = local(e.touches[0]), b = local(e.touches[1]);
          d0 = Math.hypot(a.x - b.x, a.y - b.y) || 1; k0 = k;
          mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; pan = null; moved = 99;
          e.preventDefault();
        } else if (e.touches.length === 1) {
          var p = local(e.touches[0]); moved = 0;
          pan = k > 1.001 ? { x: p.x, y: p.y } : null;
        }
      }, { passive: false });

      vp.addEventListener('touchmove', function (e) {
        if (!vp.classList.contains('zoomable')) return;
        if (e.touches.length === 2 && mid) {
          var a = local(e.touches[0]), b = local(e.touches[1]);
          var d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
          var m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          // follow the midpoint as well as the spread, so a pinch can also drag
          tx += m.x - mid.x; ty += m.y - mid.y; mid = m;
          zoomTo(k0 * (d / d0), m.x, m.y);
          e.preventDefault();
        } else if (e.touches.length === 1 && pan) {
          var p = local(e.touches[0]);
          tx += p.x - pan.x; ty += p.y - pan.y; pan = { x: p.x, y: p.y };
          moved += 1; apply(); e.preventDefault();
        }
      }, { passive: false });

      vp.addEventListener('touchend', function (e) {
        if (e.touches.length < 2) mid = null;
        if (e.touches.length === 0) {
          pan = null;
          if (moved < 2 && vp.classList.contains('zoomable')) {
            var now = Date.now();
            if (now - lastTap < 300) {
              var p = local(e.changedTouches[0]);
              if (k > 1.001) reset(); else zoomTo(2.5, p.x, p.y);
              lastTap = 0;
            } else lastTap = now;
          }
        }
      });

      // trackpad pinch and ctrl+wheel, so a desktop can read the drawing too
      vp.addEventListener('wheel', function (e) {
        if (!vp.classList.contains('zoomable') || !e.ctrlKey) return;
        var p = local(e); zoomTo(k * (e.deltaY < 0 ? 1.12 : 1 / 1.12), p.x, p.y); e.preventDefault();
      }, { passive: false });

      var md = null;
      vp.addEventListener('mousedown', function (e) {
        if (k <= 1.001 || !vp.classList.contains('zoomable')) return;
        md = local(e); e.preventDefault();
      });
      window.addEventListener('mousemove', function (e) {
        if (!md) return;
        var p = local(e);
        tx += p.x - md.x; ty += p.y - md.y; md = p; apply();
      });
      window.addEventListener('mouseup', function () { md = null; });

      /* Zoom is a phone affordance: on a desktop the drawing is already big enough,
         and touch-action:none there would make it a dead zone for scrolling. */
      var mq = matchMedia('(max-width: 767px)');
      function gateZoom() {
        vp.classList.toggle('zoomable', mq.matches);
        if (!mq.matches) reset();
      }
      if (mq.addEventListener) mq.addEventListener('change', gateZoom); else mq.addListener(gateZoom);
      gateZoom(); apply();
      return reset;
    }

    /* ---- the quick view -------------------------------------------------- */

    function makeQV(host) {
      var wrap = document.createElement('div');
      wrap.innerHTML = '<div class="qvs" data-qv-scrim hidden></div>'
        + '<aside class="qv" data-qv hidden aria-modal="true" role="dialog"'
        + ' aria-label="Part details"><div class="qvh"><b>Part details</b>'
        + '<button type="button" class="qvx" data-qv-close aria-label="Close">'
        + '<svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg></button></div>'
        + '<div class="qvb" data-qv-body></div></aside>';
      var scrim = wrap.firstChild, dr = wrap.lastChild;
      /* Inside the frame, not on the body: the [hidden] reset that actually hides this
         is .aps-frame scoped, and a drawer appended to the body never gets it. */
      host.appendChild(scrim); host.appendChild(dr);
      var body = dr.querySelector('[data-qv-body]'), last = null;

      function canSlide() {
        return !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
      function drop() { dr.classList.remove('enter'); scrim.classList.remove('enter'); }
      // Hidden mid slide, the transition stops where it stood. Landing it on return
      // costs one listener and saves a drawer stranded half off the page.
      document.addEventListener('visibilitychange', function () { if (document.hidden) drop(); });
      function close() {
        dr.hidden = true; scrim.hidden = true;
        document.documentElement.style.overflow = '';
        if (last && last.isConnected) last.focus();
        last = null;
      }
      dr.querySelector('[data-qv-close]').addEventListener('click', close);
      scrim.addEventListener('click', close);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !dr.hidden) close();
      });

      return function (f, p, opener) {
        var st = STK[p.v] || STK[0];
        var stocked = p.p != null;
        body.innerHTML =
          '<div class="qvim">' + (p.cal ? '<span class="cal">' + esc(p.cal) + '</span>' : '')
          + (p.img ? '<img alt="" src="' + att(bigOf(p.img)) + '">' : '') + '</div>'
          + '<h3>' + esc(p.t) + '</h3>'
          + (stocked ? '<div class="qvid">Product # ' + esc(p.s) + '</div>' : '')
          + (stocked ? '<div class="qvpr"><span class="pp">' + money(p.p) + '</span>'
            + '<span class="pstk ' + st[0] + '">' + esc(st[1]) + '</span></div>' : '')
          /* Buying comes before reading about it. The overview used to sit between the
             price and this row, and with a square picture above it that put the button
             around 830px down: off the bottom of the drawer on any laptop, reachable
             only by scrolling a panel most people would not guess scrolls. */
          + (stocked && p.v !== 3
            ? '<div class="qvbuy">'
              + (p.v === 2 ? '' : '<span class="qty2"><button type="button" aria-label="Less">&minus;</button>'
                + '<b>1</b><button type="button" aria-label="More">+</button></span>')
              + '<button type="button" class="obtn sm" data-qv-add' + (p.v === 2 ? ' disabled' : '') + '>'
              + esc(p.v === 2 ? L.notify : L.addToCart) + '</button></div>'
            : '')
          + '<div class="qvov"><b>Quick overview</b><ul>'
          + (p.role ? '<li><span>Part</span>' + esc(p.role) + '</li>' : '')
          + '<li><span>Fits</span>' + esc(f.brand + ' ' + f.name) + '</li>'
          + (f.models ? '<li><span>Models</span>' + esc(f.models) + '</li>' : '')
          + (p.cal ? '<li><span>Diagram key</span>' + esc(p.cal) + '</li>' : '')
          + (stocked ? '<li><span>Sold as</span>Each</li>'
            + '<li><span>Availability</span>' + esc(st[1]) + '</li>' : '')
          + '</ul></div>'
          + (stocked
            ? '<a class="qvfull" href="' + att(p.u) + '">' + esc(L.fullDetails)
              + '<svg viewBox="0 0 24 24"><path d="M5 12h13M13 6l6 6-6 6"/></svg></a>'
            : '');
        body.querySelectorAll('.qty2').forEach(stepper);
        var add = body.querySelector('[data-qv-add]');
        if (add) {
          add.addEventListener('click', function () {
            addToCart([{ id: p.vid, quantity: qtyOf(body.querySelector('.qvbuy')) }], add, null);
          });
        }
        last = opener || null;
        /* The entry frame is only safe where there is a clock to run it off. A hidden
           tab freezes animations and transitions alike, and a drawer whose position is
           the far end of a frozen transition never arrives, so it opens in place. */
        if (canSlide()) {
          dr.classList.add('enter'); scrim.classList.add('enter');
          scrim.hidden = false; dr.hidden = false;
          setTimeout(drop, 20);
        } else {
          drop(); scrim.hidden = false; dr.hidden = false;
        }
        document.documentElement.style.overflow = 'hidden';
        var x = dr.querySelector('[data-qv-close]');
        if (x) x.focus();
        body.scrollTop = 0;
      };
    }
  });
})();
